/** @format */

import produce from "immer";
import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { useUpdateResourceReferenceMutation } from "../../../app/services/resourcesApi";
import { useUpdateUserResourceReferenceMutation } from "../../../app/services/userResourceApi";
import { ResourceReference, ResourceType, UserResourceReference } from "../../../types";
import { ReactComponent as TagIcon } from "bootstrap-icons/icons/tag.svg";
import { ReactComponent as X } from "bootstrap-icons/icons/x.svg";
import { ReactComponent as Plus } from "bootstrap-icons/icons/plus.svg";
import "./EditUserResourceModal.scss";
import HelperTooltip from "../helperTooltip/HelperTooltip";
import {
  getQualificationKeystages,
  isSecureLinkUndefinedWrapper,
  qualIdToFull,
} from "../../../utils/functions";
import SpecAccordion from "../specAccordion/SpecAccordion";
import { getObjectDiff } from "./ObjectDiff";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetUserQuery } from "../../../app/services/userApi";
import HelpLink from "../../helpLink/HelpLink";
import VisualEditor from "../../VisualEditor/VisualEditor";
import { getDefaultResourceDescription } from "../resourceDetails/defaultDescriptions";
import { DeepPartial } from "@reduxjs/toolkit";

type Props = {
  resourceRef: UserResourceReference | ResourceReference;
};

const EditResourceRefModal: React.FC<Props> = ({ resourceRef }) => {
  const { t: _ } = useTranslation("translation", { keyPrefix: "editResourceRefModal"});

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user, isLoading: isUserLoading } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );
  const isStaff =
    !isUserLoading &&
    user !== undefined &&
    (user.role === "wjec" || user.role === "admin");

  const [data, setData] = useState<UserResourceReference | ResourceReference>({
    ...resourceRef,
    Status: resourceRef.Status ?? "draft",
  });

  // Keeps the modal data in sync with any changes
  useEffect(() => {
    setData({ ...resourceRef, Status: resourceRef.Status ?? "draft" });
  }, [resourceRef]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);

  const [title, setTitle] = useState<string>(data.Object.title || "");
  const defaultDescription = getDefaultResourceDescription(data.Locale, (data.Type as ResourceType), title)

  const [description, setDescription] = useState<string>(
    !!(data.Object.description?.trim()) ? data.Object.description :  defaultDescription,
  );

  const [updateUserResourceReference, { isLoading: isUserResourceLoading }] =
    useUpdateUserResourceReferenceMutation();
  const [updateResourceReference, { isLoading: isResourceLoading }] =
    useUpdateResourceReferenceMutation();

  const isLoading: boolean = isResourceLoading || isUserResourceLoading;

  const isInvalidThumbnail: boolean = !Boolean(
    !data.Object.thumbnail || data.Object.thumbnail.startsWith("https://"),
  );

  const handleClose = () => {
    setData(resourceRef);
    setShowModal(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === true) {
      // NOTE: any is used here on purpose not being lazy
      let refDiff: DeepPartial<ResourceReference> = getObjectDiff(resourceRef as any, data as any);

      if (Object.keys(refDiff).length === 0) {
        handleClose();
        setValidated(true);
        return;
      } // Only send request if changes are found

      if (
        resourceRef.PK.includes("USER:") &&
        resourceRef.Copy !== undefined &&
        (resourceRef as UserResourceReference).User !== undefined
      ) {
        await updateUserResourceReference({
          userEmailHash: (resourceRef as UserResourceReference).User ?? "",
          locale: resourceRef.Locale,
          copy: resourceRef.Copy,
          code: resourceRef.Code,
          primaryKey: { PK: resourceRef.PK, SK: resourceRef.SK },
          diff: refDiff,
        });

      } else if (isStaff && !resourceRef.PK.includes("USER:")) {
        await updateResourceReference({
          primaryKey: { PK: resourceRef.PK, SK: resourceRef.SK },
          refInformation: {
            locale: resourceRef.Locale,
            type: resourceRef.Type,
            code: resourceRef.Code,
          },
          changes: refDiff,
        });
      } 
    }

    handleClose();
    setValidated(true);
  };

  const handleObjectChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let name: string = e.target.name;
    let val: string = e.target.value;

    setData({ ...data, Object: { ...data.Object, [name]: val } });
  };

  const handleItemChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let name: string = e.target.name;
    let val: string = e.target.value;

    setData({ ...data, [name]: val });
  };

  const handleLevelChange = (selectedLevel: string, checked: boolean) => {
    let levels = data.Object.levels;

    if (!checked && levels.includes(selectedLevel)) {
      setData({
        ...data,
        Object: {
          ...data.Object,
          levels: [
            ...levels.slice(0, levels.indexOf(selectedLevel)),
            ...levels.slice(levels.indexOf(selectedLevel) + 1),
          ],
        },
      });
    } else if (checked && !levels.includes(selectedLevel)) {
      setData({
        ...data,
        Object: { ...data.Object, levels: [...levels, selectedLevel] },
      });
    }
  };

  const handleRadioChange = (eTarget: Element) => {
    setData({
      ...data,
      Object: {
        ...data.Object,
        fundedBy:
          eTarget.id === "N/A" ||
          eTarget.id === "Adnodd" ||
          eTarget.id === "WelshGov"
            ? eTarget.id
            : "N/A",
      },
    });
  };

  const handleUpdateAuthors = (value: string) => {
    if (isStaff)
      setData(
        produce(data, (draft) => {
          draft.Object.authors = value;
        }),
      );
  };

  const handleUpdateTag = (value?: string, index?: number) => {
    if (isStaff) {
      // EDIT
      if (index !== undefined) {
        setData(
          produce(data, (draft) => {
            draft.Object.tags[index] = value ?? "";
          }),
        );
      }
      // ADD
      else {
        setData(
          produce(data, (draft) => {
            if (!draft.Object.tags) {
              draft.Object.tags = [];
            }
            draft.Object.tags.push("");
          }),
        );
      }
    }
  };

  const handleRemoveTag = (index: number) => {
    if (isStaff)
      setData(
        produce(data, (draft) => {
          draft.Object.tags.splice(index, 1);
        }),
      );
  };

  return (
    <>
      <Button
        aria-label={`edit ${
          resourceRef.PK.includes("USER:") ? "user-resource" : "resource"
        } details`}
        variant="editing"
        className="px-3 w-100"
        onClick={() => {
          setValidated(false);
          setShowModal(true);
        }}
      >
        {_("editDetails")}
      </Button>
      <Modal
        show={showModal}
        onHide={() => handleClose()}
        className="resource-ref-modal"
        aria-label="edit-resource-ref-modal"
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <span className="flex-grow-1">
            {resourceRef.PK.includes("USER:")
              ? _("userResourceDetails")
              : _("resourceDetails")}
          </span>
          <HelpLink
            position="inherit"
            className="p-0 help-link"
            locationId="publishing-to-live"
          />
        </Modal.Header>
        <Form
          role={"form"}
          onSubmit={handleSubmit}
          validated={validated}
          noValidate
        >
          <Modal.Body>
            <Form.Group>
              <h5>
                <span style={{ color: "red" }}>*</span> {_("title")}
              </h5>
              <Form.Control
                name="title"
                aria-describedby={_("titleInput")}
                className="mb-4"
                value={data.Object.title || ""}
                onChange={(e) => {
                  setTitle(e.currentTarget.value);
                  handleObjectChange(e as React.ChangeEvent<HTMLInputElement>);
                }}
                aria-label={_("titleInput")}
                isInvalid={!title}
              />
            </Form.Group>

            <Form.Group>
              <div className="d-flex flex-row align-items-center justify-content-between">
                <h5>
                  <span style={{ color: "red" }}>*</span> {_("description")}
                </h5>
                { /** Added button to insert default description into description field
                 * NOTE: Will overwrite existing text
                 */}
                <Button
                  className="link-editing"
                  variant="none"
                  onClick={() => {
                    setDescription(defaultDescription);
                    setData({ ...data, Object: { ...data.Object, description: defaultDescription} });
                  }}
                >
                  {_("insertDefaultDescription")}
                </Button>
              </div>
              <VisualEditor 
                data-testid="description"
                className={`mb-4 w-100`}
                data={description}
                onChange={(_, editor) => {
                  try {
                    const _newData = editor.getData()
                    setDescription(_newData);
                    setData({
                      ...data,
                      Object: { ...data.Object, description: _newData}
                    });
                  } catch (e) { 
                    console.warn("Failed to fetch data from Editor")
                  }
                }}
              />
            </Form.Group>

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <>
                <Form.Group className="mb-3">
                  <h5>
                    {_("image")}
                    <HelperTooltip
                      type="conditionalSync"
                      tooltipText={_("conditionalSyncInformation")}
                    />
                  </h5>
                  <Form.Label>
                    {_("cloudberryLink")}
                  </Form.Label>
                  <Form.Control
                    name="thumbnail"
                    aria-describedby="Resource image"
                    isInvalid={
                      !isSecureLinkUndefinedWrapper(data.Object.thumbnail || "")
                    }
                    onChange={(e) =>
                      handleObjectChange(
                        e as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                    value={data.Object.thumbnail ?? ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    {_("secureLinkRequired")}
                  </Form.Control.Feedback>
                </Form.Group>
                {data.Object.thumbnail && !isInvalidThumbnail && (
                  <div
                    className="mb-3 rounded position-relative"
                    style={{
                      backgroundImage: `url('${data.Object.thumbnail}')`,
                      backgroundPosition: "center",
                      paddingTop: "35%",
                      backgroundSize: "cover",
                    }}
                  ></div>
                )}
              </>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group className="mb-4">
                <h5>
                  {_("acknowledgements")}
                  <HelperTooltip
                    type="conditionalSync"
                    tooltipText={_("conditionalSyncInformation")}
                  />
                  <HelperTooltip
                    tooltipText={_("acknowledgementsInformation")}
                  />
                </h5>
                <Form.Control
                  name="acknowledgements"
                  aria-describedby="Resource acknowledgements"
                  value={data.Object.acknowledgements?.toLocaleString() || ""}
                  isInvalid={
                    !isSecureLinkUndefinedWrapper(
                      data.Object.acknowledgements?.toLocaleString() || "",
                    )
                  }
                  onChange={(e) =>
                    handleObjectChange(e as React.ChangeEvent<HTMLInputElement>)
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {_("secureLinkRequired")}
                </Form.Control.Feedback>
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group>
                <h5>
                  {_("branding")}
                  <HelperTooltip
                    type="sync"
                    tooltipText={_("syncInformation")}
                  />
                </h5>
                <Form.Select
                  name="Branding"
                  className="mb-4"
                  value={data.Branding}
                  onChange={(e) => handleItemChange(e)}
                >
                  <option value="" disabled>
                    {_("selectBrand")}
                  </option>
                  <option value="cbac&wjec">WJEC & CBAC</option>
                  <option value="cbac">CBAC</option>
                  <option value="wjec">WJEC</option>
                  <option value="eduqas">Eduqas</option>
                </Form.Select>
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group
                className="mb-4"
                onChange={(e) => {
                  handleRadioChange(e.target as Element);
                }}
              >
                <h5>
                  {_("fundedBy")}
                  <HelperTooltip
                    type="sync"
                    tooltipText={_("syncInformation")}
                  />
                  <HelperTooltip tooltipText={_("fundedByInformation")} />
                </h5>

                <Form.Check
                  defaultChecked={data.Object.fundedBy === "N/A"}
                  inline
                  label={_("notApplicable")}
                  type="radio"
                  name="fundedBy"
                  id="N/A"
                />

                <Form.Check
                  defaultChecked={data.Object.fundedBy === "Adnodd"}
                  inline
                  label="Adnodd"
                  type="radio"
                  name="fundedBy"
                  id="Adnodd"
                />

                <Form.Check
                  defaultChecked={data.Object.fundedBy === "WelshGov"}
                  inline
                  label={_("welshGovernment")}
                  type="radio"
                  name="fundedBy"
                  id="WelshGov"
                />
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group className="mb-4">
                <h5>{_("qualification")}</h5>
                <Form.Control
                  value={qualIdToFull(data.Qualification)}
                  disabled={true}
                />
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group
                className="mb-4"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleLevelChange(e.target.id, e.target.checked)
                }
              >
                <h5>
                  {_("keyStages")}
                  <HelperTooltip
                    type="sync"
                    tooltipText={_("syncInformation")}
                  />
                  <HelperTooltip tooltipText={_("keystageInformation")} />
                </h5>
                {["KS2", "KS3", "KS4", "KS5"].map(
                  (keystage: string, index: number) => (
                    <Form.Check
                      key={`${keystage}-${index}`}
                      label={`${keystage}`}
                      name="levels"
                      type="checkbox"
                      id={keystage}
                      // Assumes that the incoming data contains the correct default keystage
                      defaultChecked={
                        Array.isArray(data.Object.levels) &&
                        data.Object.levels?.includes(keystage)
                      }
                      disabled={
                        data.Qualification
                          ? getQualificationKeystages(
                              data.Qualification,
                            ).includes(keystage)
                          : false
                      }
                    />
                  ),
                )}
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group>
                <h5>
                  {_("specItem")}
                  <HelperTooltip
                    type="sync"
                    tooltipText={_("syncInformation")}
                  />
                  <HelperTooltip tooltipText={_("specItemInformation")} />
                </h5>
                <SpecAccordion
                  handleSelectedChange={(e) => handleItemChange(e)}
                  SubjectId={data.Subject.id}
                  Qualification={data.Qualification}
                  DefaultSelected={data.SpecItem}
                />
              </Form.Group>
            )}

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group>
                <h5>
                  {_("author")}
                  <HelperTooltip
                    type="conditionalSync"
                    tooltipText={_("conditionalSyncInformation")}
                  />
                  <HelperTooltip tooltipText={_("authorInformation")} />
                </h5>
                <Form.Control
                  name="author"
                  aria-describedby={_("Resource author input")}
                  className="mb-4"
                  value={data.Object.authors || ""}
                  onChange={(e) => handleUpdateAuthors(e.currentTarget.value)}
                />
              </Form.Group>
            )}

            <Form.Group>
              <h5>{_("synopsis")}</h5>
              <Form.Control
                name="synopsis"
                aria-describedby={_("descriptionInput")}
                className="mb-4"
                value={data.Object.synopsis}
                onChange={(e) =>
                  handleObjectChange(e as React.ChangeEvent<HTMLInputElement>)
                }
                as="textarea"
                rows={3}
              />
            </Form.Group>

            <Form.Group>
              <h5>{_("tags")}</h5>
              <div className="tags mb-4 d-flex gap-2 flex-wrap">
                {data.Object.tags &&
                  data.Object.tags.map((tag, tagIndex) => (
                    <div
                      className="tag-container d-flex"
                      id={`tag-${tagIndex + 1}`}
                      key={`${tagIndex + 1}`}
                    >
                      <InputGroup>
                        <InputGroup.Text className="tag">
                          <TagIcon />
                        </InputGroup.Text>
                        <Form.Control
                          value={tag || ""}
                          disabled={!isStaff}
                          onChange={(e: any) => {
                            handleUpdateTag(e.currentTarget.value, tagIndex);
                          }}
                          aria-label="Tag input"
                        />
                        {isStaff && (
                          <Button
                            className="delete-tag"
                            variant="light"
                            onClick={() => {
                              handleRemoveTag(tagIndex);
                            }}
                            aria-label={_("deleteTag")}
                          >
                            <X className="tag-delete-icon" />
                          </Button>
                        )}
                      </InputGroup>
                    </div>
                  ))}
                {isStaff && (
                  <Button
                    variant="outline-editing"
                    size="sm"
                    onClick={() => handleUpdateTag()}
                    aria-label={_("addTag")}
                  >
                    <Plus />
                  </Button>
                )}
              </div>
            </Form.Group>

            {!resourceRef.PK.includes("USER:") && isStaff && (
              <Form.Group className="mb-4">
                <h5>{_("status")}</h5>
                <Form.Select
                  name="Status"
                  onChange={(e) => handleItemChange(e)}
                  value={data.Status}
                >
                  <option value={"draft"}>{_("draft")}</option>
                  <option value={"live"}>{_("live")}</option>
                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className="buttons-container  justify-content-end">
              <div className="form-action-buttons d-flex gap-2">
                <Button
                  variant="outline-editing"
                  disabled={isLoading}
                  onClick={() => handleClose()}
                >
                  {_("cancel")}
                </Button>
                {
                  <Button
                    aria-label="save"
                    className="outline-editing"
                    variant={`${isLoading ? "outline-editing" : "editing"}`}
                    disabled={isLoading || !title || !description}
                    type="submit"
                  >
                    {isLoading ? <Spinner /> : _("save")}
                  </Button>
                }
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default EditResourceRefModal;
